<template>
  <section id="chapter" :class="[
      'chapter',
      'page-common', 
      `detect_deviece_${deviceType}`, 
      `state_${deviceType}`,
      `theme-${domainConfig?.designType || 1}`,
    ]">
    <div class="chapter-container">
      <!-- 顶部导航 -->
      <div class="chapter-header">
        <div class="chapter-nav" @click="navigateToBookDetail">
          <div class="chapter-book-title">{{ bookTitle }}</div>
          <svg width="5" height="8" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg"
            class="chapter-back-arrow">
            <path id="Vector" d="M0.541992 0.5L4.04199 4L0.541992 7.5" stroke="#2C3337"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <!-- 右侧悬浮按钮 -->
        <div class="chapter-actions">
          <div class="chapter-action-btn" @click="navigateToHome">
            <img src="@/assets/home.svg" alt="Home">
          </div>
          <div class="chapter-action-btn" @click="toggleChapterList">
            <img src="@/assets/list.svg" alt="Chapter List">
          </div>
        </div>
      </div>

      <!-- 主要内容区域 -->
      <div class="chapter-content">
        <!-- PC端前一章按钮 - 只在PC设备且不是第一章时显示 -->
        <div v-if="!isFirstChapter && deviceType != 'mobile'" class="chapter-prev"
          @click="navigateToPreviousChapter()">
          <img src="@/assets/vector-left.svg" alt="Previous Chapter">
          <span>Previous Chapter</span>
        </div>

        <!-- 章节内容 -->
        <div class="chapter-main">
          <h1 class="chapter-title">Chapter {{ chapterNum }}：{{ chapterTitle }}</h1>
          <img :src="chapterData.summaryInfo.bigImg" v-if="deviceType == 'mobile'"
            class="chapter-cover" alt="">

          <AdComponent v-if="deviceType == 'mobile'"
            v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.chapter_1"
            ref="ads-chapter-1" class="m_padding ad_chapter" :ads="adsensConfig?.chapter_1"
            :showDebug="showDebug" @adHidden="handleAdHidden" />

          <!-- 加载状态显示 -->
          <div v-if="loading" class="chapter-loading">
            <div class="chapter-loading-spinner"></div>
            <p>Loading chapter content...</p>
          </div>

          <!-- 错误消息显示 -->
          <div v-else-if="error" class="chapter-error">
            <p>{{ error }}</p>
            <button @click="fetchChapterData" class="chapter-retry-btn">Retry</button>
          </div>

          <!-- PC端显示完整内容，移动端使用分页 -->
          <template v-else-if="deviceType != 'mobile'">
            <!-- 使用过渡效果实现章节切换动画 -->
            <transition :name="'chapter-page-transition' + (isReverse ? '-reverse' : '')"
              mode="out-in">
              <div :key="chapterNum" class="chapter-text" v-html="fullChapterContent">
              </div>
            </transition>
          </template>
          <template v-else>
            <!-- 使用过渡效果实现翻页动画 -->
            <transition :name="'chapter-page-transition' + (isReverse ? '-reverse' : '')"
              mode="out-in">
              <div :key="chapterNum + '-page-' + currentPageIndex"
                class="chapter-text chapter-text-mobile" v-html="currentPageContent"></div>
            </transition>
          </template>
        </div>

        <!-- PC端下一章按钮 - 只在PC设备且不是最后一章时显示 -->
        <div v-if="!isLastChapter && deviceType != 'mobile'" class="chapter-next"
          @click="navigateToNextChapter()">
          <span>Next Chapter</span>
          <img src="@/assets/vector-right.svg" alt="Next Chapter">
        </div>

        <!-- 触摸区域 - 只在移动设备显示 -->
        <div v-if="deviceType === 'mobile'" class="chapter-page-triggers">
          <div class="chapter-page-trigger-prev" @click="prevPage"></div>
          <div class="chapter-page-trigger-next" @click="nextPage"></div>
        </div>
      </div>

      <!-- 移动端底部导航 - 只在移动设备显示 -->
      <div v-if="deviceType === 'mobile'" class="chapter-footer">
        <div class="chapter-footer-container">
          <div class="chapter-footer-nav" @click="navigateToPreviousChapter(false)"
            v-if="!isFirstChapter">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <g id="arrow_right">
                <path id="Vector" d="M8.45801 3.5L4.95801 7L8.45801 10.5" stroke="#CE5236"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </g>
            </svg>
            <span>Previous Chapter</span>
          </div>
          <!-- 占位元素，确保在没有前一章按钮时，下一章按钮仍然右对齐 -->
          <div v-else class="chapter-footer-placeholder"></div>
          <div class="chapter-footer-nav chapter-footer-right" @click="navigateToNextChapter(false)"
            v-show="!isLastChapter">
            <span>Next Chapter</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <g id="arrow_right">
                <path id="Vector" d="M4.95801 3.5L8.45801 7L4.95801 10.5" stroke="#CE5236"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      <!-- 章节列表弹窗 -->
      <div v-if="showChapterList" class="chapter-list-overlay" @click.self="toggleChapterList">
        <div class="chapter-list-container">
          <!-- 固定的顶部标题和总数 -->
          <div class="chapter-list-header">
            <div class="chapter-list-title">Chapters</div>
            <div class="chapter-list-total">
              Total: {{ displayedTotalChapters }}
            </div>
          </div>

          <!-- 可滚动的章节列表 -->
          <div class="chapter-list-content">
            <div v-for="chapter in filteredChaptersList" :key="chapter.number"
              :class="['chapter-list-item', Number(chapterNum) === chapter.number ? 'chapter-list-item--active' : '']"
              @click="selectChapter(chapter)">
              <div class="chapter-list-item-number">Chapter {{ chapter.number }}</div>
              <div class="chapter-list-item-title">{{ chapter.title }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast 提示 -->
      <div class="chapter-toast" v-if="showToastMessage">
        <div class="chapter-toast-content">
          {{ toastMessage }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import common from '@/mixin/common.js'
import adMixin from '@/mixin/adMixin.js'
import adTrackingMixin from '@/mixin/adTrackingMixin.js'
import AdComponent from '@/pages/components/AdComponent.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'Chapter',
  components: {
    AdComponent
  },
  mixins: [common, adMixin, adTrackingMixin],
  props: {
    summaryId: {
      type: [String, Number],
      required: false
    },
    chapterNum: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      bookTitle: '',
      chapterTitle: '',
      showChapterList: false,
      // 书籍信息
      bookInfo: {
        totalChapters: 0
      },
      chaptersList: [],
      // 章节内容（分页显示）
      chapterPages: [],
      currentPageIndex: 0,
      apiPageData: [], // 存储API返回的分页数据
      isReverse: false,  // 控制翻页方向
      chapterTransitionInProgress: false,
      readingPositionKey: 'chapter_reading_position',
      loading: false,
      error: null,
      hasNext: false, // 是否有下一章
      chapterData: null, // 当前章节的完整数据
      // 添加控制选项，默认不显示索引为0且标题为"Cover"的章节
      showCoverChapter: false,
      // Toast提示相关
      showToastMessage: false,
      toastMessage: '',
      toastTimer: null,

      // 分页方式：'char'按字符数分页，'height'按高度分页
      paginationType: 'height',

      // 调试信息
      lastPaginationTime: 0,
      paginationCount: 0
    }
  },
  computed: {
    ...mapGetters([
      "adsensConfig",
      'adPosConfig',
      'deviceType',
      'domainConfig',
    ]),
    // 判断是否为第一章
    isFirstChapter () {
      return Number(this.chapterNum) <= 1;
    },
    // 判断是否为最后一章
    isLastChapter () {
      // 如果API显示没有下一章，或者章节号大于等于总章节数，则为最后一章
      return !this.hasNext || (this.bookInfo.totalChapters > 0 && Number(this.chapterNum) >= this.bookInfo.totalChapters);
    },
    // 判断是否为当前章的第一页
    isFirstPage () {
      return this.currentPageIndex <= 0;
    },
    // 判断是否为当前章的最后一页
    isLastPage () {
      return this.currentPageIndex >= this.chapterPages.length - 1;
    },
    // 获取上一章编号
    prevChapterNum () {
      return Number(this.chapterNum) - 1;
    },
    // 获取下一章编号
    nextChapterNum () {
      return Number(this.chapterNum) + 1;
    },
    // 获取当前页内容
    currentPageContent () {
      return this.chapterPages[this.currentPageIndex] || '';
    },
    // 获取完整章节内容（PC端使用）
    fullChapterContent () {
      if (this.apiPageData.length > 0) {
        // 合并所有API分页内容
        return this.apiPageData.map(page => page.content).join('<br/><hr/><br/>');
      }
      return this.chapterPages.join('');
    },
    // 过滤章节列表，根据showCoverChapter决定是否显示Cover章节
    filteredChaptersList () {
      if (this.showCoverChapter) {
        return this.chaptersList;
      } else {
        // 过滤掉索引为0且标题为Cover的章节
        return this.chaptersList.filter(chapter =>
          !(chapter.number === 0 || chapter.number === '0') ||
          !chapter.title.toLowerCase().includes('cover'));
      }
    },
    // 添加一个计算属性，用于显示总章节数
    displayedTotalChapters () {
      // 如果显示Cover章节，就显示全部数量；否则显示总数减去Cover章节(如果有的话)
      const hasCoverChapter = this.chaptersList.some(chapter =>
        (chapter.number === 0 || chapter.number === '0') &&
        chapter.title.toLowerCase().includes('cover')
      );

      return this.showCoverChapter ?
        this.bookInfo.totalChapters :
        hasCoverChapter ? this.bookInfo.totalChapters : this.chaptersList.length;
    }
  },
  methods: {
    // 显示Toast提示
    showToast (message) {
      this.toastMessage = message;
      this.showToastMessage = true;

      // 清除之前的定时器
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }

      // 设置新的定时器，2秒后自动关闭
      this.toastTimer = setTimeout(() => {
        this.showToastMessage = false;
      }, 2000);
    },

    // 获取章节数据
    async fetchChapterData () {
      this.loading = true;
      this.error = null;

      try {
        // 调用API获取章节数据
        const params = {
          summaryId: this.summaryId,
          chapterNum: Number(this.chapterNum)
        };

        console.log('获取章节数据，参数:', params);

        // 调用接口获取数据
        const response = await this.$global.server.getBookDetail(params);

        if (response && response.code == 200) {
          this.chapterData = response.data;

          // 更新章节信息
          this.bookTitle = this.chapterData.summaryInfo?.novelName || 'Unknown Book';
          this.novelId = this.chapterData.summaryInfo?.novelId || '';

          // 存储API返回的分页数据
          if (this.chapterData.chapterList && Array.isArray(this.chapterData.chapterList)) {
            this.apiPageData = this.chapterData.chapterList;

            // 如果有分页数据，使用第一页的章节名称
            if (this.apiPageData.length > 0) {
              this.chapterTitle = this.apiPageData[0].chapterName || `Chapter ${this.chapterNum}`;
            } else {
              this.chapterTitle = `Chapter ${this.chapterNum}`;
            }
          } else {
            this.apiPageData = [];
            this.chapterTitle = `Chapter ${this.chapterNum}`;
          }

          this.hasNext = this.chapterData.hasNext || false;

          // 处理章节列表
          if (this.chapterData.summaryInfo && this.chapterData.summaryInfo.novelChapter) {
            this.processChapterList(this.chapterData.summaryInfo.novelChapter);
          }

          // 处理章节内容
          const content = this.getChapterContent();
          // console.log('章节内容:', content);
          await this.processChapterContent(content);

          console.log('page init ready');
          // 如果URL请求跳转到最后一页
          if (this.$route.query.goToLastPage === 'true') {
            this.currentPageIndex = this.chapterPages.length - 1;
          }

          // 显示页码信息
          if (this.deviceType === 'mobile') {
            this.showPageNumber();
          }

        } else {
          this.error = response?.msg || 'Error code 1';
          console.error('API 错误:', response);

          // 使用本地测试内容作为备份
          // this.useLocalContent();
        }
      } catch (error) {
        this.error = 'Error code 2';
        console.error('获取章节数据错误:', error);

        // 使用本地测试内容作为备份
        // this.useLocalContent();
      } finally {
        this.loading = false;
      }
    },

    // 获取章节内容（合并API分页内容）
    getChapterContent () {
      // 如果有API分页数据，合并所有内容
      if (this.apiPageData.length > 0) {
        return this.apiPageData.map(page => page.content).join('\n\n');
      }

      // 如果没有API数据但有章节内容
      if (this.chapterData && this.chapterData.content) {
        return this.chapterData.content;
      }

    },

    // 处理章节列表数据
    processChapterList (chapters) {
      if (!chapters || !Array.isArray(chapters)) {
        this.chaptersList = [];
        this.bookInfo.totalChapters = 0;
        return;
      }

      // 处理章节列表
      this.chaptersList = chapters.map((chapter, index) => {
        // 判断chapter是字符串还是对象
        if (typeof chapter === 'string') {
          // 解析格式如: "0.Cover" 或 "1.Chapter Name"
          const match = chapter.match(/^(\d+)\.(.+)$/);

          if (match) {
            const chapterNumber = parseInt(match[1], 10);
            const chapterTitle = match[2].trim();

            return {
              id: index,
              number: chapterNumber,
              title: chapterTitle,
              link: `/chapter/${this.novelId}/${this.summaryId}/${chapterNumber}`
            };
          }
        }

        // 默认情况，使用索引作为章节号
        return {
          id: index,
          number: index,
          title: typeof chapter === 'string' ? chapter : `Chapter ${index + 1}`,
          link: `/chapter/${this.novelId}/${this.summaryId}/${index}`
        };
      });

      // 检查是否存在Cover章节
      const hasCoverChapter = this.chaptersList.some(chapter =>
        (chapter.number === 0 || chapter.number === '0') &&
        chapter.title.toLowerCase().includes('cover')
      );

      // 更新总章节数，如果存在Cover章节则总数减1
      this.bookInfo.totalChapters = hasCoverChapter ?
        this.chaptersList.length - 1 :
        this.chaptersList.length;
    },

    // 处理章节内容
    async processChapterContent (content) {
      if (!content) {
        this.chapterPages = ['<p>No content available for this chapter.</p>'];
        return;
      }

      // 如果是PC设备，不需要分页，直接展示完整内容
      if (this.deviceType !== 'mobile') {
        this.chapterPages = [content];
        return;
      }

      // 处理移动端分页
      console.log('处理移动端分页，内容长度:', content.length);

      // // 根据分页类型选择分页方式
      // if (this.paginationType === 'char') {
      //   console.log('使用字符数分页方式');
      //   this.paginateByChar(content);
      // } else {
      //   console.log('使用高度分页方式');
      //   this.paginateByHeight(content);
      // }
      this.$nextTick(() => {
        this.paginateByHeight(content);
      });
      // setTimeout(() => {
      //   this.paginateByHeight(content);
      // }, 1000);

      // 重置当前页索引
      this.currentPageIndex = 0;

    },
    // 处理广告隐藏事件
    handleAdHidden () {
      console.log('广告被隐藏，重新计算分页');
      // 获取当前章节内容
      const content = this.getChapterContent();
      if (content) {
        // 延迟一点执行，确保DOM已完全更新
        setTimeout(() => {
          this.paginateByHeight(content);
          // 如果是从上一章跳转过来的，并且要显示最后一页
          if (this.$route.query.goToLastPage === 'true') {
            this.$nextTick(() => {
              this.currentPageIndex = Math.max(0, this.chapterPages.length - 1);
              console.log('跳转到最后一页:', this.currentPageIndex);
            });
          }
        }, 100);
      }
    },

    // 章节切换前的准备工作
    prepareChapterTransition (isReverse = false) {
      // 设置翻页方向
      this.isReverse = isReverse;
      // 标记正在进行章节切换
      this.chapterTransitionInProgress = true;
    },

    // 返回首页
    navigateToHome () {
      this.$router.push('/');
    },

    // 切换章节列表显示状态
    toggleChapterList () {
      this.showChapterList = !this.showChapterList;
    },

    // 选择章节
    selectChapter (chapter) {
      if (Number(this.chapterNum) !== chapter.number) {
        // 确定翻页方向
        this.isReverse = Number(this.chapterNum) > chapter.number;
        this.chapterTransitionInProgress = true;

        // 使用setTimeout确保动画完成后再改变URL
        setTimeout(() => {
          // 使用完整的路由参数：novelId、summaryId和章节编号
          this.$router.replace(`/chapter/${this.novelId}/${this.summaryId}/${chapter.number}`);
        }, 300); // 动画持续时间
      }
      this.showChapterList = false;
    },

    // 下一页
    nextPage () {
      if (this.currentPageIndex >= this.chapterPages.length - 1) {
        // 如果是最后一页，跳转到下一章
        if (!this.isLastChapter) {
          this.navigateToNextChapter(true);
        } else {
          // 如果已经是最后一章最后一页，显示提示
          this.showToast("This is the last page.");
        }
      } else {
        this.isReverse = false;
        this.currentPageIndex++;
        // 显示页码信息
        this.showPageNumber();
      }
    },

    // 上一页
    prevPage () {
      if (this.currentPageIndex <= 0) {
        // 如果是第一页，跳转到上一章的最后一页
        if (!this.isFirstChapter) {
          this.navigateToPreviousChapter(true);
        } else {
          // 如果已经是第一章第一页，显示提示
          this.showToast("This is the first page.");
        }
      } else {
        this.isReverse = true;
        this.currentPageIndex--;
        // 显示页码信息
        this.showPageNumber();
      }
    },

    // 跳转到上一章
    navigateToPreviousChapter (toLastPage = false) {
      if (!this.isFirstChapter) {
        // 设置动画方向为反向
        this.isReverse = true;
        this.chapterTransitionInProgress = true;

        // 防止路由变化导致动画丢失
        // 使用完整的路由参数：novelId、summaryId和章节编号
        const targetPath = `/chapter/${this.novelId}/${this.summaryId}/${this.prevChapterNum}`;
        // 通过设置goToLastPage参数，确保跳转到上一章的最后一页
        const query = toLastPage ? { goToLastPage: 'true' } : {};

        // 使用setTimeout确保动画完成后再改变URL
        setTimeout(() => {
          this.$router.replace({
            path: targetPath,
            query: query
          });
        }, 300); // 动画持续时间
      }
    },

    // 跳转到下一章
    navigateToNextChapter (fromLastPage = false) {
      if (!this.isLastChapter) {
        // 设置动画方向为正向
        this.isReverse = false;
        this.chapterTransitionInProgress = true;

        // 防止路由变化导致动画丢失
        // 使用完整的路由参数：novelId、summaryId和章节编号
        const targetPath = `/chapter/${this.novelId}/${this.summaryId}/${this.nextChapterNum}`;
        // 对于从最后一页跳转到下一章的情况，我们不需要特殊处理，始终应该显示第一页
        const query = fromLastPage ? { fromLastPage: 'true' } : {};

        // 使用setTimeout确保动画完成后再改变URL
        setTimeout(() => {
          this.$router.replace({
            path: targetPath,
            query: query
          });
        }, 300); // 动画持续时间
      }
    },

    // 处理路由变化
    handleRouteChange () {
      // 当路由变化时重新加载章节数据
      // this.fetchChapterData();

      // 重置章节切换标记
      this.chapterTransitionInProgress = false;
    },

    // 跳转到小说详情页
    navigateToBookDetail () {
      // 从章节数据中获取分类信息，如果没有则使用"fiction"作为默认值
      const categoryParam = (this.chapterData?.summaryInfo?.novelType || 'fiction').toLowerCase().replace(/\s+/g, '-');
      const novelId = this.novelId;
      const idParam = this.summaryId;

      // 使用路由格式：/:categories/:novelId/:id
      this.$router.push(`/${categoryParam}/${novelId}/${idParam}`);
    },

    // 显示当前页码和总页数
    showPageNumber () {
      const currentPage = this.currentPageIndex + 1;
      const totalPages = this.chapterPages.length;
      // this.showToast(`Page ${currentPage}/${totalPages}`);
      console.log(`Page ${currentPage}/${totalPages}`);
    },

    // 切换分页方式
    togglePaginationType () {
      // 切换分页方式
      this.paginationType = this.paginationType === 'char' ? 'height' : 'char';

      // 重新加载分页
      this.fetchChapterData();

      // 显示提示
      // this.showToast(`分页方式: ${this.paginationType === 'char' ? '按字符数 (600字/页)' : '按屏幕高度 (自适应)'}`);
    },

    // 按高度分页处理内容
    paginateByHeight (content) {
      // 计算实际可用内容区域高度
      const calculateAvailableHeight = () => {
        // 获取视窗高度
        const viewportHeight = window.innerHeight;
        console.log('视窗高度:', viewportHeight, 'px');

        // 获取各元素高度
        const headerHeight = document.querySelector('.chapter-header')?.offsetHeight || 0;
        const titleHeight = document.querySelector('.chapter-title')?.offsetHeight || 0;
        const coverHeight = document.querySelector('.chapter-cover')?.offsetHeight || 0;

        // 获取广告元素
        const adElement = document.querySelector('.ad_chapter');
        let adHeight = 0;
        // 确保至少有一行文字的高度
        let minTextHeight = 0; // 至少一行文字的高度（包含行高）

        // 检查广告是否显示
        if (adElement && getComputedStyle(adElement).display !== 'none' && adElement.offsetHeight > 0) {
          adHeight = adElement.offsetHeight;
          minTextHeight = 20
          console.log('广告可见且高度为:', adHeight, 'px');
        } else {
          console.log('广告不可见或高度为0');
        }

        const footerHeight = document.querySelector('.chapter-footer')?.offsetHeight || 0;

        const footerElement = document.querySelector('.chapter-footer');
        let footerPaddingTop = 0;
        let footerPaddingBottom = 0;
        if (footerElement) {
          const footerStyle = window.getComputedStyle(footerElement);
          footerPaddingTop = parseFloat(footerStyle.paddingTop) || 0;
          footerPaddingBottom = parseFloat(footerStyle.paddingBottom) || 0;
        }

        console.log(footerPaddingTop, footerPaddingBottom);

        // 获取内容区域的padding
        const contentElement = document.querySelector('.chapter-content');
        let contentPaddingTop = 0;
        let contentPaddingBottom = 0;

        if (contentElement) {
          const contentStyle = window.getComputedStyle(contentElement);
          contentPaddingTop = parseFloat(contentStyle.paddingTop) || 0;
          // contentPaddingBottom = parseFloat(contentStyle.paddingBottom) || 0;
        }

        // 计算各高度
        console.log('章节头部高度:', headerHeight, 'px');
        console.log('章节标题高度:', titleHeight, 'px');
        console.log('封面标题高度:', coverHeight, 'px');
        console.log('广告高度:', adHeight, 'px');
        console.log('底部导航高度:', footerHeight, 'px');
        console.log('内容区域上内边距:', contentPaddingTop, 'px');
        console.log('内容区域下内边距:', contentPaddingBottom, 'px');

        // 额外安全边距，避免内容过于紧凑
        const safetyMargin = 30;

        // 计算实际可用高度
        const availableHeight = viewportHeight - headerHeight - titleHeight - coverHeight - adHeight - footerHeight - footerPaddingTop - footerPaddingBottom - contentPaddingTop - contentPaddingBottom - safetyMargin;

        const finalHeight = Math.max(availableHeight, minTextHeight);

        console.log('实际可用高度:', availableHeight, 'px');
        console.log('最终使用高度:', finalHeight, 'px');

        return finalHeight;
      };

      // 计算最大页面高度为可用高度
      const MAX_PAGE_HEIGHT = calculateAvailableHeight();

      console.log(getComputedStyle(document.documentElement).fontSize);
      // 转换回rem以保持一致性
      const MAX_PAGE_HEIGHT_REM = MAX_PAGE_HEIGHT / parseFloat(getComputedStyle(document.documentElement).fontSize);

      console.log('开始按高度分页，最大高度:', MAX_PAGE_HEIGHT, 'px (', MAX_PAGE_HEIGHT_REM, 'rem)');

      // 记录当前分页状态，用于调试
      this.lastPaginationTime = Date.now();
      console.log('分页计算开始时间:', new Date().toISOString());

      // 检查内容是否为空
      if (!content || content.trim() === '') {
        console.warn('分页内容为空');
        this.chapterPages = ['<p>No content available for this chapter.</p>'];
        return;
      }

      // 创建临时DOM元素用于测量高度，尽量模拟实际渲染环境
      const tempDiv = document.createElement('div');
      tempDiv.className = 'chapter-text chapter-text-mobile'; // 使用与实际渲染相同的类
      tempDiv.style.width = '100%';
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.padding = '0 .427rem'; // 使用移动端对应的内边距
      tempDiv.style.fontSize = '.3733rem'; // 适配移动端字体大小
      tempDiv.style.lineHeight = '.96rem'; // 使用实际行高

      // 处理内容中的换行符
      let processedContent = content
        .replace(/\r\n/g, '\n') // 统一换行符为 \n
        .replace(/\n/g, '<br>'); // 将换行符转换为 <br> 标签

      // 如果内容不包含HTML标签，则包装在p标签中
      if (!/<[a-z][\s\S]*>/i.test(processedContent)) {
        processedContent = `<p>${processedContent}</p>`;
      }

      tempDiv.innerHTML = processedContent;
      document.body.appendChild(tempDiv);

      const totalHeight = tempDiv.offsetHeight;
      console.log('内容总高度:', totalHeight, 'px');

      // 预计页数
      const estimatedPages = Math.ceil(totalHeight / MAX_PAGE_HEIGHT);
      console.log('预计页数:', estimatedPages);

      const pages = [];

      // 添加调试信息
      document.body.setAttribute('data-chapter-info',
        `viewport:${window.innerHeight}px, content-height:${MAX_PAGE_HEIGHT}px, pages:${estimatedPages}`);

      // 记录分页开始时间和内容总长度
      console.log('分页开始时间:', new Date().toISOString());
      console.log('内容总长度:', processedContent.length, '字符');

      // 使用更精确的分页算法
      let remainingContent = processedContent;
      let pageIndex = 0;

      // 记录每页的实际字符数，用于调试
      const pageSizes = [];

      while (remainingContent.length > 0) {
        // 如果剩余内容不多，直接作为最后一页
        if (remainingContent.length < 500) {
          pages.push(`<div class="height-limited-content" style="min-height: 50px; height: ${MAX_PAGE_HEIGHT}px;"><p>${remainingContent}</p></div>`);
          pageSizes.push(remainingContent.length);
          console.log(`第 ${pageIndex + 1} 页内容长度: ${remainingContent.length} 字符 (最后一页)`);
          break;
        }

        // 二分查找适合当前页高度的内容量
        let left = 100; // 至少包含100个字符
        let right = Math.min(remainingContent.length, 2000); // 最多2000个字符，避免过长
        let mid, height;
        let bestPos = 0;

        while (left <= right) {
          mid = Math.floor((left + right) / 2);
          tempDiv.innerHTML = `<p>${remainingContent.substring(0, mid)}</p>`;
          height = tempDiv.offsetHeight;

          if (height <= MAX_PAGE_HEIGHT) {
            bestPos = mid;
            left = mid + 1;
          } else {
            right = mid - 1;
          }
        }

        // 确保找到了合适的分割点
        if (bestPos === 0) {
          bestPos = Math.max(100, right); // 至少包含100个字符
        }

        // 尝试在句子结尾处分页
        let pageContent = remainingContent.substring(0, bestPos);
        const lastPeriod = pageContent.lastIndexOf('. ');
        const lastQuestion = pageContent.lastIndexOf('? ');
        const lastExclamation = pageContent.lastIndexOf('! ');
        const lastBreak = pageContent.lastIndexOf('<br>');

        // 找到最后的句子结尾或换行符
        let breakPoint = Math.max(lastPeriod, lastQuestion, lastExclamation, lastBreak);

        // 如果找到了句子结尾或换行符且不是太靠前（至少有总内容的80%）
        if (breakPoint > bestPos * 0.80) {
          bestPos = breakPoint + (breakPoint === lastBreak ? 4 : 2); // <br>是4个字符，句点是2个字符
        } else {
          // 尝试在空格处分页
          const lastSpace = pageContent.lastIndexOf(' ');
          if (lastSpace > bestPos * 0.90) {
            bestPos = lastSpace + 1;
          }
        }

        // 获取最终的页面内容
        pageContent = remainingContent.substring(0, bestPos);
        pageSizes.push(pageContent.length);

        // 添加页面内容
        pages.push(`<div class="height-limited-content" style="min-height: 50px; height: ${MAX_PAGE_HEIGHT}px;"><p>${pageContent}</p></div>`);
        console.log(`第 ${pageIndex + 1} 页内容长度: ${pageContent.length} 字符`);

        // 更新剩余内容和页码
        remainingContent = remainingContent.substring(bestPos);
        pageIndex++;
      }

      // 输出所有页面的字符数统计
      console.log('所有页面字符数:', pageSizes.join(', '));
      console.log('平均每页字符数:', pageSizes.reduce((a, b) => a + b, 0) / pageSizes.length);

      // 检查最后一页是否过长
      if (pageSizes.length > 1) {
        const lastPageSize = pageSizes[pageSizes.length - 1];
        const avgOtherPages = pageSizes.slice(0, -1).reduce((a, b) => a + b, 0) / (pageSizes.length - 1);

        if (lastPageSize > avgOtherPages * 1.5) {
          console.warn('警告：最后一页字符数异常，是平均值的', (lastPageSize / avgOtherPages).toFixed(2), '倍');

          // 如果最后一页过长，可以考虑进一步分割
          if (lastPageSize > 600) {
            const lastPageContent = remainingContent;
            const midPoint = Math.floor(lastPageContent.length / 2);

            // 尝试在句子结尾处分割
            const firstHalf = lastPageContent.substring(0, midPoint);
            const lastPeriod = firstHalf.lastIndexOf('. ');
            const lastQuestion = firstHalf.lastIndexOf('? ');
            const lastExclamation = firstHalf.lastIndexOf('! ');
            const lastBreak = firstHalf.lastIndexOf('<br>');

            let splitPoint = Math.max(lastPeriod, lastQuestion, lastExclamation, lastBreak);
            if (splitPoint > midPoint * 0.7) {
              splitPoint += (splitPoint === lastBreak ? 4 : 2);

              // 移除最后一页
              pages.pop();

              // 添加分割后的两页
              const part1 = lastPageContent.substring(0, splitPoint);
              const part2 = lastPageContent.substring(splitPoint);

              pages.push(`<div class="height-limited-content" style="min-height: 50px; height: ${MAX_PAGE_HEIGHT}px;"><p>${part1}</p></div>`);
              pages.push(`<div class="height-limited-content" style="min-height: 50px; height: ${MAX_PAGE_HEIGHT}px;"><p>${part2}</p></div>`);

              console.log('已将过长的最后一页分割为两页:', part1.length, '和', part2.length, '字符');
            }
          }
        }
      }

      // document.body.removeChild(tempDiv);
      this.chapterPages = pages;
      console.log(`创建了 ${pages.length} 页内容`);
    },
  },
  async mounted () {
    // 在组件创建时处理分页
    await this.fetchChapterData();
    // await this.initAdSystem();
    // // 确保在广告系统初始化后再初始化跟踪
    // this.$nextTick(() => {
    //   this.initAdTracking();
    // });

  },

  beforeDestroy () {
    // 组件销毁前清除定时器
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  },

  // 当路由参数变化时，更新页面状态
  beforeRouteUpdate (to, from, next) {
    next();
    // 处理路由变化后的状态更新
    this.$nextTick(() => {
      this.handleRouteChange();
      this.fetchChapterData(); // 重新加载章节数据
    });
  },

  // 路由进入完成后的钩子
  async beforeRouteEnter (to, from, next) {
    next(vm => {
      // 如果是从上一章跳转过来，并且要显示最后一页
      if (to.query.goToLastPage === 'true') {
        console.log('路由进入：准备跳转到最后一页');
        // 在数据加载完成后执行
        const checkAndSetLastPage = () => {
          if (vm.chapterPages && vm.chapterPages.length > 0) {
            vm.currentPageIndex = vm.chapterPages.length - 1;
            console.log('设置为最后一页:', vm.currentPageIndex);
          } else {
            // 如果页面还没准备好，稍后再试
            setTimeout(checkAndSetLastPage, 1500);
          }
        };

        // 延迟执行，确保内容已加载
        setTimeout(checkAndSetLastPage, 1000);
      }
    });
  },
}
</script>

<style lang="scss">
@import '@/css/chapter.scss';
</style> 